import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EASE } from "../../lib/scroll";

const DURATION = 2600;
const WORD = "BYTEVISION".split("");

const Rings = () => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="absolute rounded-full border border-[#d4af37]"
        initial={{ width: 40, height: 40, opacity: 0 }}
        animate={{ width: [40, 520 + i * 160], height: [40, 520 + i * 160], opacity: [0, 0.35, 0] }}
        transition={{ duration: 2.6, ease: "easeOut", delay: 0.25 + i * 0.35, repeat: Infinity, repeatDelay: 0.4 }}
      />
    ))}
  </div>
);

const Scanline = () => (
  <motion.div
    className="pointer-events-none absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-[#e8c979] to-transparent"
    initial={{ top: "0%", opacity: 0 }}
    animate={{ top: ["0%", "100%"], opacity: [0, 0.6, 0.6, 0] }}
    transition={{ duration: 2.2, ease: "easeInOut", delay: 0.2 }}
  />
);

export const Preloader = ({ onDone }) => {
  const [count, setCount] = useState(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / DURATION, 1);
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => { setShow(false); onDone(); }, 350);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="preloader"
          className="grain fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-[#050505] text-[#fafaf7]"
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          initial={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.2, ease: EASE }}
        >
          {/* golden aura */}
          <motion.div
            className="pointer-events-none absolute inset-0 m-auto h-[70vmin] w-[70vmin] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.06) 35%, transparent 65%)" }}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: [0, 1, 0.75], scale: [0.4, 1.05, 1] }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.15 }}
          />
          <Rings />
          <Scanline />

          {/* eye reveal: iris opens from a horizontal slit */}
          <div className="relative flex flex-col items-center">
            <motion.div
              className="relative"
              initial={{ clipPath: "inset(49% 0 49% 0)", filter: "blur(14px)", scale: 0.92, opacity: 0 }}
              animate={{ clipPath: "inset(0% 0 0% 0)", filter: "blur(0px)", scale: 1, opacity: 1 }}
              transition={{ duration: 1.7, ease: EASE, delay: 0.35 }}
            >
              <img src="/logo-mark.png" alt="ByteVision" draggable={false} className="h-28 w-auto select-none md:h-40" />
              {/* light sweep */}
              <motion.div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.55) 50%, transparent 60%)",
                  mixBlendMode: "overlay",
                  WebkitMaskImage: "url(/logo-mark.png)",
                  maskImage: "url(/logo-mark.png)",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                }}
                initial={{ x: "-120%" }}
                animate={{ x: "120%" }}
                transition={{ duration: 1.1, ease: "easeInOut", delay: 1.6 }}
              />
            </motion.div>

            {/* hairline */}
            <motion.span
              className="mt-8 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 260, opacity: 1 }}
              transition={{ duration: 1.2, ease: EASE, delay: 1.1 }}
            />

            {/* wordmark, letter by letter */}
            <div className="mt-6 flex font-display text-[13px] font-medium md:text-sm" aria-label="ByteVision">
              {WORD.map((ch, i) => (
                <motion.span
                  key={i}
                  className={i >= 4 ? "text-[#E8C979]" : "text-[#fafaf7]"}
                  initial={{ opacity: 0, y: 14, letterSpacing: "0.1em", filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, letterSpacing: "0.5em", filter: "blur(0px)" }}
                  transition={{ duration: 0.9, ease: EASE, delay: 1.25 + i * 0.05 }}
                >
                  {ch}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="absolute bottom-10 left-6 right-6 flex items-end justify-between md:left-14 md:right-14">
            <motion.span
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.8 }}
              className="font-mono text-[11px] tracking-[0.3em] text-white/40"
            >
              NEW SPACE · NEW AVIATION
            </motion.span>
            <span className="font-display text-6xl font-light tabular-nums gold-text md:text-8xl">{count}</span>
          </div>
          <div className="absolute bottom-0 left-0 h-px bg-[#d4af37]" style={{ width: `${count}%` }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
